import { useContext, useState, useEffect } from 'react';

import * as Sentry from '@sentry/react';
import BubbleLoader from 'App/Components/Loaders/BubbleLoader';
import { useDialogTabTrap, useModalPortal } from 'App/hooks';
import { LanguageContext } from 'App/Providers/games';

import polyglot from '../../../localization';
import FullscreenModalFrame from '../FullscreenModalFrame';

import './styles.scss';

/**
 * Full-viewport "18+ only" promo with partner rows (Figma Overlay+Widgets).
 *
 * @param {SharpDataStarService} sharpstarService - Sharpstar service instance.
 * @param {string} rid - rule id.
 * @param {object} urlParams
 * @param {object} botInfo
 * @param {() => void} [onMisclicked]
 * @param {string} [className] - Optional class name for the modal layer
 */
export default function AgeRestrictionPromoModal({
    sharpstarService,
    rid,
    botInfo,
    urlParams,
    onMisclicked,
    className = '',
}) {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { locale } = useContext(LanguageContext);

    const { portalEl, dialogRef } = useModalPortal({
        rootClassName: 'age-restriction-promo-modal',
        bodyLockClass: 'age-restriction-promo-modal-is-open',
        portalMarker: 'age-restriction-promo',
    });

    useDialogTabTrap(dialogRef);

    useEffect(() => {
        if (!sharpstarService || typeof botInfo.isBot !== 'boolean') return;

        const fetchOffers = async () => {
            try {
                const { refid1, refid2, refid3 } = urlParams;
                const { isBot, failedChecks, visitorId, customIsBot } = botInfo;

                const response = await sharpstarService.getOffers({
                    botFailedChecks: failedChecks,
                    hash: undefined,
                    isLead: 0,
                    customIsBot,
                    visitorId,
                    isBot,
                    refid1,
                    refid2,
                    refid3,
                    rid,
                });

                setOffers(response.offers);
            } catch (e) {
                Sentry.captureException(e);
            } finally {
                setLoading(false);
            }
        };

        void fetchOffers();
    }, [sharpstarService, botInfo.isBot]);

    return (
        <FullscreenModalFrame
            portalEl={portalEl}
            dialogRef={dialogRef}
            layerClassName={`age-restriction-promo-modal__layer ${className}`}
            backdropClassName='age-restriction-promo-modal__backdrop'
            dialogClassName='age-restriction-promo-modal__dialog'
            dialogProps={{
                'role': 'dialog',
                'aria-modal': true,
                'aria-labelledby': 'age-restriction-promo-modal-title',
                'aria-describedby': 'age-restriction-promo-modal-subtitle',
                'tabIndex': -1,
            }}
            afterDialog={
                <a
                    className='age-restriction-promo-modal__misclicked-link'
                    href='.'
                    onClick={(event) => {
                        event.preventDefault();
                        onMisclicked?.();
                    }}>
                    {polyglot.t(`${locale}.ageRestrictionPromo.misclicked`)}
                </a>
            }>
            {loading && (
                <BubbleLoader
                    loaderColor={'#09328d'}
                    backgroundColor={'#e4effe'}
                />
            )}

            <header className='age-restriction-promo-modal__header'>
                <h2
                    className='age-restriction-promo-modal__title'
                    id='age-restriction-promo-modal-title'>
                    {polyglot.t(`${locale}.ageRestrictionPromo.title`)}
                </h2>

                {!!offers.length && (
                    <p
                        className='age-restriction-promo-modal__subtitle'
                        id='age-restriction-promo-modal-subtitle'>
                        {polyglot.t(`${locale}.ageRestrictionPromo.subtitle`)}{' '}
                        👇
                    </p>
                )}
            </header>

            <div className='age-restriction-promo-modal__widgets'>
                {!!offers.length &&
                    offers.map((offer) => {
                        const {
                            id,
                            link,
                            content: { logo, button, title, subtitle },
                        } = offer;

                        return (
                            <div
                                key={id}
                                className='age-restriction-promo-modal__widget'>
                                <div className='age-restriction-promo-modal__widget-brand'>
                                    <img
                                        className='age-restriction-promo-modal__widget-logo'
                                        src={logo}
                                        alt={title || ''}
                                        width={180}
                                        height={50}
                                    />
                                </div>

                                <div className='age-restriction-promo-modal__widget-text'>
                                    <p className='age-restriction-promo-modal__widget-name'>
                                        {title}
                                    </p>

                                    <p className='age-restriction-promo-modal__widget-desc'>
                                        {subtitle}
                                    </p>
                                </div>

                                <a
                                    className='age-restriction-promo-modal__cta'
                                    href={link}
                                    target='_blank'
                                    rel='noopener noreferrer'>
                                    {button}
                                </a>
                            </div>
                        );
                    })}
            </div>
        </FullscreenModalFrame>
    );
}
